import { useEffect, useState, useRef } from 'react';
import parse from 'html-react-parser';
import { CacheProvider } from '@emotion/react';
import { getMUICache, getImageUrl, getUrl, isLoggedIn, getEndpoint, setCmsSeoData, isCSR, isUpdatedWithin24Hours, getTimeAgo, getImageId, getImageSize } from '../../../common/utils';
import { SocialShareTwo } from '../SocialShareTwo/SocialShareTwo';
import './liveBlog.css';
import { URL_PROTOCAL } from '../../Common/Constant';
import { EagerLoadImages, AddImageDimensions, SetHighPriorityImages, PreloadViewportImages, ImageLoader } from '../../IntersectionObserver/imageComponent';
import { Button } from '@mui/material';
import { PAGEBUILDER_URLS } from '../../Common/Constant';
import RefreshIcon from '@mui/icons-material/Refresh';
import Poll from './poll';

liveBlog.version = 1000;

liveBlog.lang = 'en';
liveBlog.langs = [
    'en'
];
liveBlog.setLang = (lang: any) => {
    liveBlog.lang = lang;
}

Object.defineProperty(liveBlog, 'name', {
    writable: true,
    value: 'liveBlog'
});


export function liveBlog(props: any) {

    const{imagecroptype} = props

    EagerLoadImages();
    AddImageDimensions();
    SetHighPriorityImages();
    PreloadViewportImages();
    ImageLoader();

    const [imageIdObj, setImageIdObj] = useState<any>({});
    const [liveblogData, setLiveblogData] = useState<any>([]);
    const [loading, setLoading] = useState(false);
    const [error] = useState(null);
    const [newUpdatesCount, setNewUpdatesCount] = useState(0);
    const [loadingUpdates, setLoadingUpdates] = useState(false);
    const currentCountRef = useRef(0);
    // const [selectedOption, setSelectedOption] = useState(null);

    let REQUEST_HOSTNAME: any = null;
    if (props && props.REQUEST_HOSTNAME) {
        REQUEST_HOSTNAME = props.REQUEST_HOSTNAME;
    }

    let REQUEST_URL: any = null;
    if (props && props.REQUEST_URL) {
        REQUEST_URL = props.REQUEST_URL;
    }

    // Polling interval in milliseconds (10 seconds)
    const POLLING_INTERVAL = 10000;
    // Ref for polling interval
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Extract the child count from the API response
    const getChildCount = (data: any) => {
        if (Array.isArray(data?.response?.data) && data.response.data.length > 0 && data.response.data[0] && data.response.data[0].childcount) {
            return data.response.data[0].childcount;
        } else {
            return 0;
        }
    };

    const setCount = (count: any) => {
        if (count) {
            currentCountRef.current = count;
        }
    };

    const addExportedData = (object: any) => {
        if (typeof props.exportedKeys !== "undefined" && typeof props.exportedKeys.pid !== "undefined"
            && object.parentid && object.parentid != "") {
            props.exportedKeys.pid = object.parentid;
        }
        if (typeof props.exportedKeys !== "undefined" && typeof props.exportedKeys.sectionid !== "undefined"
            && object.msid && object.msid != "") {
            props.exportedKeys.sectionid = object.msid;
        }
        if (typeof props.exportedKeys !== "undefined" && typeof props.exportedKeys.tags !== "undefined"
            && Array.isArray(object.keywords) && object.keywords.length > 0) {
            const names = object.keywords.map((item: any) => item.name).join(',');
            props.exportedKeys.tags = names;
        }
    }

    const endpoint = getEndpoint(props, "MAIN");
    const contentURL = getUrl(props, 1, false, 'MAIN', true, "", "", false);
    const countURL = getUrl(props, 1, false, 'MAIN', true, "", "", false).replace("/content/", "/count/");

    if (endpoint.isSSR && endpoint.SSRDataObj && liveblogData && Object.keys(liveblogData).length === 0
        && endpoint.SSRDataObj.response && Array.isArray(endpoint.SSRDataObj.response.data) && endpoint.SSRDataObj.response.data.length > 0) {
        setLiveblogData(endpoint.SSRDataObj.response.data[0]);
        addExportedData(endpoint.SSRDataObj.response.data[0]);
        setCount(getChildCount(endpoint.SSRDataObj));
        setCmsSeoData(props, endpoint.SSRDataObj.response.data[0], "LiveblogShow");
    }

    // Format timestamp to readable format with IST
    const formatDate = (timestamp: any, formatType: 'short' | 'long' = 'short') => {
        if (!timestamp) return '';

        const date = new Date(parseInt(timestamp));

        if (formatType === 'short') {
            // Format: 10:53 (IST) | Jun 03, 25
            const time = date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            const dateStr = date.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: '2-digit'
            });

            return `${time} (IST) | ${dateStr}`;
        }

        // Format: April 5, 2024, 12:11 PM (without "at")
        const datePart = date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });

        const timePart = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        return `${datePart}, ${timePart}`;
    };

    // Fetch the liveblog content
    const fetchLiveblogContent = async () => {
        try {
            const response = await fetch(contentURL, {
                method: 'GET',
                cache: 'no-store'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching liveblog content:', error);
            throw error;
        }
    };

    // Fetch the liveblog count data
    const fetchLiveblogCount = async () => {
        try {
            const response = await fetch(countURL, {
                method: 'GET',
                cache: 'no-store'

            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching liveblog count:', error);
            throw error;
        }
    };

    // Check for updates
    const checkForUpdates = async () => {
        try {
            const countData = await fetchLiveblogCount();
            const newCount = getChildCount(countData);

            // console.log(`Current count: ${currentCountRef.current}, New count: ${newCount}`);

            if (newCount > currentCountRef.current) {
                // New updates available
                setNewUpdatesCount(newCount - currentCountRef.current);
            }
        } catch (error) {
            console.error('Error checking for updates:', error);
        }
    };

    // Handle notification button click
    const handleNotificationClick = async () => {
        try {
            // Show loading state
            setLoadingUpdates(true);

            // Fetch and display the latest content
            const data = await fetchLiveblogContent();
            setLiveblogData(data?.response?.data?.[0]);

            // Update the current count
            setCount(getChildCount(data));

            // Reset new updates count
            setNewUpdatesCount(0);
        } catch (error) {
            console.error('Error loading updates:', error);
            //setError('Failed to load updates. Try again.');
        } finally {
            setLoadingUpdates(false);
        }
    };

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);

            try {
                const data = await fetchLiveblogContent();
                setLiveblogData(data?.response?.data?.[0]);

                // Set initial child count
                setCount(getChildCount(data));
            } catch (error) {
                console.error('Error loading initial data:', error);
                //setError('Failed to load liveblog data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (!endpoint.isSSR || !endpoint.SSRDataObj) {
            loadInitialData();
        }

        if (liveblogData && liveblogData.primeId) {
            isLoggedIn();
        }

        if (!(isCSR() && PAGEBUILDER_URLS.some(url => window.location.origin.includes(url)))) {
            // Start polling for updates
            pollingIntervalRef.current = setInterval(checkForUpdates, POLLING_INTERVAL);
            console.log('Polling started');

            // Clean up on unmount
            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    console.log('Polling stopped');
                }
            };
        }
    }, []);

    useEffect(() => {

        let imageIdObj = {
            msid: liveblogData?.msid,
            thumbsize: liveblogData?.thumbsize
        }
        if (props.hostImageId && (!imageIdObj.msid || !imageIdObj.thumbsize)) {
            imageIdObj.msid =  getImageId(props.hostImageId);
            imageIdObj.thumbsize = getImageSize(props.hostImageId);
        }

        setImageIdObj(imageIdObj);

        renderLiveblogItems();
    }, [liveblogData]);

    const isLive = isUpdatedWithin24Hours(liveblogData?.metainfo?.LastPublishMilliTime?.value, liveblogData?.updatedate);

    // Render liveblog items
    const renderLiveblogItems = () => {
        if (!liveblogData || !liveblogData.children || liveblogData.children.length === 0) {
            return <div className='loader'>No updates available yet.</div>;
        }

        return liveblogData.children.map((item: any, index: number) => (
            <div className={`blogBlockWrapper ${index === 0 ? 'firstItem' : ''}`} key={item.msid || index}>

                {/* Render content based on cmstype */}
                {item.cmstype === "IMAGES" && (
                    <div className='insideBlogImageMainWrapper' id={`image-${item.msid}`}>
                        <div className='blogBlockDate'>{formatDate(item.updatedate)}</div>
                        <div className='imageBlogTitle blogBlockHeading'>{item.title || ''}</div>

                        <div className='insideBlogImagesWrapper'>
                            {item.thumbsize && item.msid && item.thumbsize > 0 &&
                                <div key={item.msid || index} className='insideBlogImage'>
                                    <picture>
                                        <source media="(min-width: 768px)" srcSet={getImageUrl(item.msid, item.thumbsize, 760, 428, "", false, props.CONSTANTS, imagecroptype)} />
                                        <source media="(max-width: 767px)" srcSet={getImageUrl(item.msid, item.thumbsize, 328, 185, "", false, props.CONSTANTS, imagecroptype)} />
                                        <img alt={item.title} title={item.title} />
                                    </picture>
                                </div>}
                            <div className='insideBlogSynopsis'>
                                {item.synopsis && (
                                    <p dangerouslySetInnerHTML={{ __html: item.synopsis || '' }}></p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {item.cmstype === "TEXTSNIPPET" &&

                    <>
                        <div className='blogBlockDate'>{formatDate(item.updatedate)}</div>
                        <div className='text-snippet-container' id={`text-snippet-${item.msid}`}>
                            <div className='blogBlockHeading' dangerouslySetInnerHTML={{ __html: item.title || '' }} />
                            <div className='blogBlockContentWrapper'>
                                <p dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} ></p>
                            </div>
                        </div></>
                }

                {item.cmstype === "QUOTES" &&
                    <div className='quote-container' id={`quote-${item.msid}`}>
                        <div className='time'>{formatDate(item.insertdate)}</div>
                        <div className='blogBlockContentWrapper quote'>
                            <p>{item.title}</p>
                        </div>
                        <div className='blogBlockContentWrapper'>{item.quotedby}</div>
                    </div>
                }
                {item.cmstype === "MEDIAVIDEO" && item?.media?.rmname === "youtube" &&
                    <div className='video-container' id={`video-${item.msid}`}>
                        <div className='time'>{formatDate(item.insertdate)}</div>
                        <div className='blogBlockHeading'>{item.title}</div>
                        <div><iframe width="100%" height="388" src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=YT${item.media.id}&autoplay=0`} title={item.title} frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe></div>
                        <div className="blogBlockContentWrapper" dangerouslySetInnerHTML={{ __html: item.description || '' }} />
                    </div>
                }

                {item.cmstype === "MEDIAVIDEO" && item?.media?.rmname === "Kaltura" &&
                    <div className='video-container' id={`video-${item.msid}`}>
                        <div className='time'>{formatDate(item.insertdate)}</div>
                        <div className='blogBlockHeading'>{item.title}</div>
                        <div><iframe width="100%" height="388" src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=${item.media.id}&autoPlay=0`} title={item.title} frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div>
                        <div className='blogBlockContentWrapper' dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} />
                    </div>
                }
                {item.cmstype === "MEDIAAUDIO" && item?.media?.rmname && (


                    <>                {item.media.rmname === "youtube" && (
                        <div className='audio-container youtube-audio-embed' id={`audio-yt-${item.msid}`}>
                            <div className='time'>{formatDate(item.insertdate)}</div>
                            <div className='blogBlockHeading'>{item.title}</div>
                            <div>
                                <iframe
                                    width="100%"
                                    height="388" // Or a height more suitable for audio player if different
                                    src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=YT${item.media.id}&autoplay=0`}
                                    title={item.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    allowFullScreen
                                ></iframe>
                            </div>
                            <div className='blogBlockContentWrapper' dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} />
                        </div>
                    )}</>

                    // <>
                    //     {item.media.rmname === "youtube" ? (
                    //         <div className='audio-container youtube-audio-embed' id={`audio-yt-${item.msid}`}>
                    //             <div className='time'>{formatDate(item.insertdate)}</div>
                    //             <div className='blogBlockHeading'>{item.title}</div>
                    //             <div>
                    //                 <iframe
                    //                     width="100%"
                    //                     height="388" // Or a height more suitable for audio player if different
                    //                     src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=YT${item.media.id}&autoplay=0`}
                    //                     title={item.title}
                    //                     frameBorder="0"
                    //                     allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    //                     referrerPolicy="strict-origin-when-cross-origin"
                    //                     allowFullScreen
                    //                 ></iframe>
                    //             </div>
                    //             <div className='blogBlockContentWrapper' dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} />
                    //         </div>
                    //     ) : (
                    //         <div className='audio-container' id={`audio-${item.msid}`}>
                    //             <div className='time'>{formatDate(item.insertdate)}</div>
                    //             <div className='blogBlockHeading'>{item.title}</div>
                    //             <audio controls src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=${item.media.id}`}>
                    //                 Your browser does not support the audio element.
                    //             </audio>
                    //             <div className='blogBlockContentWrapper' dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} />
                    //         </div>
                    //     )}
                    // </>
                )}



                {item.cmstype === "MEDIAAUDIO" && item?.media?.rmname === "Kaltura" &&
                    <div className='audio-container' id={`audio-${item.msid}`}>
                        <div className='time'>{formatDate(item.insertdate)}</div>
                        <div className='blogBlockHeading'>{item.title}</div>
                        <div><iframe width="100%" height="388" src={`https://tvid.in/e#skipad=false&apikey=denmarka5ec9705eb7ac2c984033f264&v=${item.media.id}&autoPlay=0`} title={item.title} frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div>
                        <div className='blogBlockContentWrapper' dangerouslySetInnerHTML={{ __html: item.synopsis || '' }} />
                    </div>
                }

                {item.cmstype === "POLL" && item.polloptions && (
                    <Poll
                        item={item}
                        CONSTANTS={props.CONSTANTS}
                        formatDate={formatDate}
                    />
                )}
                {item.cmstype === "SLIDE" &&
                    item?.embeds?.[0]?.type === "javascript" &&
                    item?.embeds?.[0]?.title === "Twitter" &&
                    (() => {
                        // console.log("LiveBlog: Processing Twitter item:", item.msid, "CDATA content:", item.text);
                        let twitterEmbedHtml = '';

                        if (item.text) {
                            // const cdataMatch = item.text.match(/<cdata>\s*<!\[CDATA\[(.*?)\]\]>\s*<\/cdata>/is);
                            const cdataMatch = item.text.match(/<cdata>\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*<\/cdata>/i);
                            if (cdataMatch && cdataMatch[1]) {
                                twitterEmbedHtml = cdataMatch[1].trim();
                                // console.log("LiveBlog: Extracted Twitter HTML:", twitterEmbedHtml);
                            }
                        }

                        if (twitterEmbedHtml) {
                            const TwitterEmbedInline = () => {
                                const ref = useRef<HTMLDivElement>(null);

                                useEffect(() => {
                                    if (ref.current) {
                                        ref.current.innerHTML = twitterEmbedHtml;

                                        if (typeof window !== "undefined") {
                                            const twttr = (window as any).twttr;
                                            if (twttr && twttr.widgets) {
                                                // console.log("LiveBlog: Twitter widgets API found, loading embed for", item.msid);
                                                twttr.widgets.load(ref.current);
                                            } else {
                                                // console.log("LiveBlog: Twitter widgets API not found, loading script for", item.msid);
                                                const script = document.createElement("script");
                                                script.src = "https://platform.twitter.com/widgets.js";
                                                script.async = true;
                                                script.charset = "utf-8";
                                                document.body.appendChild(script);
                                            }
                                        }
                                    }
                                }, []);

                                return (
                                    <>
                                        <div className='time'>{formatDate(item.insertdate)}</div>
                                        <div className="twitter-cdata-container" id={`twitter-cdata-${item.msid}`}>

                                            {item.title && (
                                                <div className='blogBlockHeading'>
                                                    {item.title}
                                                </div>
                                            )}
                                            <div ref={ref} />
                                        </div></>
                                );
                            };

                            return <TwitterEmbedInline />;
                        }
                        console.warn("LiveBlog: Could not render Twitter embed for item:", item.msid, "HTML was empty or window undefined.");
                        return null;
                    })()}




                {item.cmstype === "SLIDE" && item?.embeds?.[0]?.type === "javascript" && item?.embeds?.[0]?.title === "Facebook" && (() => {
                    // console.log("LiveBlog: Processing Facebook item:", item.msid, "CDATA content:", item.text);
                    let facebookIframe = '';
                    if (item.text) {
                        const cdataMatch = item.text.match(/<cdata>\s*<!\[CDATA\[(.*?)\]\]>\s*<\/cdata>/i);
                        if (cdataMatch && cdataMatch[1]) {
                            facebookIframe = cdataMatch[1].trim();
                            // console.log("LiveBlog: Extracted Facebook iframe HTML:", facebookIframe);
                        }
                    }

                    if (facebookIframe) {
                        return (
                            <div className='facebook-cdata-container' id={`facebook-cdata-${item.msid}`}>
                                <div className='time'>{formatDate(item.insertdate)}</div>
                                <div className='blogBlockHeading'>{item.title}</div>
                                <div dangerouslySetInnerHTML={{ __html: facebookIframe }} />
                            </div>
                        );
                    }
                    console.warn("LiveBlog: Could not render Facebook embed for item:", item.msid, "Iframe HTML was empty.");
                    return null;
                })()}
                {item.cmstype === "SLIDE" &&
                    item?.embeds?.[0]?.type === "javascript" &&
                    item?.embeds?.[0]?.title === "Instagram" &&
                    (() => {
                        let instagramBlockquote = '';
                        let finalHtmlToEmbed = '';
                        // console.log("LiveBlog: Processing Instagram item:", item.msid, "CDATA content:", item.text);
                        if (item.text) {
                            // const cdataMatch = item.text.match(/<cdata>\s*<!\[CDATA\[(.*?)\]\]>\s*<\/cdata>/is);
                            const cdataMatch = item.text.match(/<cdata>\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*<\/cdata>/i);
                            if (cdataMatch && cdataMatch[1]) {
                                instagramBlockquote = cdataMatch[1].trim();
                                // console.log("LiveBlog: Extracted Instagram content:", instagramBlockquote);

                                // Check if it's a URL or a full blockquote
                                if (instagramBlockquote.startsWith('https://www.instagram.com/')) {
                                    // It's a URL, construct the blockquote
                                    const urlParts = instagramBlockquote.split('/');
                                    const shortcode = urlParts[4]; // p/SHORTCODE or reel/SHORTCODE
                                    if (shortcode) {
                                        finalHtmlToEmbed = `<blockquote class="instagram-media" data-instgrm-permalink="${instagramBlockquote}" data-instgrm-version="14" style=" background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:540px; min-width:326px; padding:0; width:99.375%; width:-webkit-calc(100% - 2px); width:calc(100% - 2px);"><div style="padding:16px;"> <a href="${instagramBlockquote}" style=" background:#FFFFFF; line-height:0; padding:0 0; text-align:center; text-decoration:none; width:100%;" target="_blank"> <div style=" display: flex; flex-direction: row; align-items: center;"> <div style="background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 40px; margin-right: 14px; width: 40px;"></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 100px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 60px;"></div></div></div><div style="padding: 19% 0;"></div> <div style="display:block; height:50px; margin:0 auto 12px; width:50px;"><svg width="50px" height="50px" viewBox="0 0 60 60" version="1.1" xmlns="https://www.w3.org/2000/svg" xmlns:xlink="https://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-511.000000, -20.000000)" fill="#000000"><g><path d="M556.869,30.41 C554.814,30.41 553.148,32.076 553.148,34.131 C553.148,36.186 554.814,37.852 556.869,37.852 C558.924,37.852 560.59,36.186 560.59,34.131 C560.59,32.076 558.924,30.41 556.869,30.41 M541,60.657 C535.114,60.657 530.342,55.887 530.342,50 C530.342,44.114 535.114,39.342 541,39.342 C546.887,39.342 551.658,44.114 551.658,50 C551.658,55.887 546.887,60.657 541,60.657 M541,33.886 C532.1,33.886 524.886,41.1 524.886,50 C524.886,58.899 532.1,66.113 541,66.113 C549.9,66.113 557.115,58.899 557.115,50 C557.115,41.1 549.9,33.886 541,33.886 M565.378,62.101 C565.244,65.022 564.756,66.606 564.346,67.663 C563.803,69.06 563.154,70.057 562.106,71.106 C561.058,72.155 560.06,72.803 558.662,73.347 C557.607,73.757 556.021,74.244 553.102,74.378 C549.944,74.521 548.997,74.552 541,74.552 C533.003,74.552 532.056,74.521 528.898,74.378 C525.979,74.244 524.393,73.757 523.338,73.347 C521.94,72.803 520.942,72.155 519.894,71.106 C518.846,70.057 518.197,69.06 517.654,67.663 C517.244,66.606 516.755,65.022 516.623,62.101 C516.479,58.943 516.448,57.996 516.448,50 C516.448,42.003 516.479,41.056 516.623,37.899 C516.755,34.978 517.244,33.391 517.654,32.338 C518.197,30.938 518.846,29.942 519.894,28.894 C520.942,27.846 521.94,27.196 523.338,26.654 C524.393,26.244 525.979,25.756 528.898,25.623 C532.057,25.479 533.004,25.448 541,25.448 C548.997,25.448 549.943,25.479 553.102,25.623 C556.021,25.756 557.607,26.244 558.662,26.654 C560.06,27.196 561.058,27.846 562.106,28.894 C563.154,29.942 563.803,30.938 564.346,32.338 C564.756,33.391 565.244,34.978 565.378,37.899 C565.522,41.056 565.552,42.003 565.552,50 C565.552,57.996 565.522,58.943 565.378,62.101 M570.82,37.631 C570.674,34.438 570.167,32.258 569.425,30.349 C568.659,28.377 567.633,26.702 565.965,25.035 C564.297,23.368 562.623,22.342 560.652,21.575 C558.743,20.834 556.562,20.326 553.369,20.18 C550.169,20.033 549.148,20 541,20 C532.853,20 531.831,20.033 528.631,20.18 C525.438,20.326 523.257,20.834 521.349,21.575 C519.376,22.342 517.703,23.368 516.035,25.035 C514.368,26.702 513.342,28.377 512.574,30.349 C511.834,32.258 511.326,34.438 511.181,37.631 C511.035,40.831 511,41.851 511,50 C511,58.147 511.035,59.17 511.181,62.369 C511.326,65.562 511.834,67.743 512.574,69.651 C513.342,71.625 514.368,73.296 516.035,74.965 C517.703,76.634 519.376,77.658 521.349,78.425 C523.257,79.167 525.438,79.673 528.631,79.82 C531.831,79.965 532.853,80.001 541,80.001 C549.148,80.001 550.169,79.965 553.369,79.82 C556.562,79.673 558.743,79.167 560.652,78.425 C562.623,77.658 564.297,76.634 565.965,74.965 C567.633,73.296 568.659,71.625 569.425,69.651 C570.167,67.743 570.674,65.562 570.82,62.369 C570.966,59.17 571,58.147 571,50 C571,41.851 570.966,40.831 570.82,37.631"></path></g></g></g></svg></div><div style="padding-top: 8px;"> <div style=" color:#3897f0; font-family:Arial,sans-serif; font-size:14px; font-style:normal; font-weight:550; line-height:18px;">View this post on Instagram</div></div><div style="padding: 12.5% 0;"></div> <div style="display: flex; flex-direction: row; margin-bottom: 14px; align-items: center;"><div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(0px) translateY(7px);"></div> <div style="background-color: #F4F4F4; height: 12.5px; transform: rotate(-45deg) translateX(3px) translateY(1px); width: 12.5px; flex-grow: 0; margin-right: 14px; margin-left: 2px;"></div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(9px) translateY(-18px);"></div></div><div style="margin-left: 8px;"> <div style=" background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 20px; width: 20px;"></div> <div style=" width: 0; height: 0; border-top: 2px solid transparent; border-left: 6px solid #f4f4f4; border-bottom: 2px solid transparent; transform: translateX(16px) translateY(-4px) rotate(30deg)"></div></div><div style="margin-left: auto;"> <div style=" width: 0px; border-top: 8px solid #F4F4F4; border-right: 8px solid transparent; transform: translateY(16px);"></div> <div style=" background-color: #F4F4F4; flex-grow: 0; height: 12px; width: 16px; transform: translateY(-4px);"></div> <div style=" width: 0; height: 0; border-top: 8px solid #F4F4F4; border-left: 8px solid transparent; transform: translateY(-4px) translateX(8px);"></div></div></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center; margin-bottom: 24px;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 224px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 144px;"></div></div></a><p style=" color:#c9c8cd; font-family:Arial,sans-serif; font-size:14px; line-height:17px; margin-bottom:0; margin-top:8px; overflow:hidden; padding:8px 0 7px; text-align:center; text-overflow:ellipsis; white-space:nowrap;"><a href="${instagramBlockquote}" style=" color:#c9c8cd; font-family:Arial,sans-serif; font-size:14px; font-style:normal; font-weight:normal; line-height:17px; text-decoration:none;" target="_blank">A post shared by Instagram</a></p></div></blockquote>`;
                                        // console.log("LiveBlog: Constructed Instagram blockquote:", finalHtmlToEmbed);
                                    } else {
                                        console.warn("LiveBlog: Could not extract shortcode from Instagram URL:", instagramBlockquote);
                                    }
                                } else if (instagramBlockquote.includes('instagram-media')) {
                                    // It's already a blockquote
                                    finalHtmlToEmbed = instagramBlockquote;
                                } else {
                                    console.warn("LiveBlog: Instagram content is not a URL or a known blockquote structure:", instagramBlockquote);
                                }
                            }
                        }

                        if (finalHtmlToEmbed) {
                            const InstagramEmbed = () => {
                                const ref = useRef<HTMLDivElement>(null);

                                useEffect(() => {
                                    if (ref.current) {
                                        ref.current.innerHTML = finalHtmlToEmbed;

                                        if (typeof window !== "undefined") {
                                            if ((window as any).instgrm?.Embeds?.process) {
                                                // console.log("LiveBlog: Instagram embed API found, processing for", item.msid);
                                                (window as any).instgrm.Embeds.process();
                                            } else {
                                                // console.log("LiveBlog: Instagram embed API not found, loading script for", item.msid);
                                                const script = document.createElement("script");
                                                script.src = "//www.instagram.com/embed.js"; // Using // to respect http/https
                                                script.async = true;
                                                script.onload = () => {
                                                    if ((window as any).instgrm?.Embeds?.process) {
                                                        (window as any).instgrm.Embeds.process();
                                                    }
                                                };
                                                document.body.appendChild(script);
                                            }
                                        }
                                    }
                                }, []);

                                return (
                                    <>
                                        <div className='time'>{formatDate(item.insertdate)}</div>
                                        <div className='instagram-cdata-container' id={`instagram-cdata-${item.msid}`}>
                                            {item.title && (
                                                <div className='blogBlockHeading'>
                                                    {item.title}
                                                </div>
                                            )}
                                            <div ref={ref} />
                                        </div></>
                                );
                            };

                            return <InstagramEmbed />;
                        }
                        console.warn("LiveBlog: Could not render Instagram embed for item:", item.msid, "Final HTML was empty.");
                        return null;
                    })()}

            </div>
        ));
    };

    return (
        <CacheProvider value={getMUICache()}>
            <div>

                <div className='liveBlogHeadingBox'>
                    <div className={`liveBlogCommonLiveHeading ${isLive ? "liveBlogAnimationHeading" : "liveBlogNoneAnimationHeading"}`}>
                        {isLive && <i></i>}
                        Live Blog{' '}
                        {isLive && (
                            <span className='blogUpdateTime'>Updated {getTimeAgo(liveblogData?.metainfo?.LastPublishMilliTime?.value, liveblogData?.updatedate)}</span>
                        )}
                    </div>
                </div>

                <div className='liveBlogMainHeadingWrapper'>
                    {liveblogData && (
                        <div className='liveBlogMainHeading'>
                            {liveblogData.title || 'Live Updates'}
                        </div>
                    )}
                </div>
                <div className='showmaintime'>
                    {/* {formatDate(liveblogData.insertdate)} | Updated:  */}
                    {liveblogData ? formatDate(liveblogData.updatedate, 'long') : ''}
                    {/* {liveblogData.agency?.id || 'NA'} */}
                </div>
                <div className='liveBlogSocialIcons blogShowMobileSocial'>
                    <SocialShareTwo
                        shareUrl={URL_PROTOCAL + REQUEST_HOSTNAME + REQUEST_URL}
                        showFacebook={true}
                        showTwitter={true}
                        showLinkedin={true}
                        showWhatsapp={true}
                        showPintrest={true} />
                </div>
                <div className="liveBlogSynopsisImageWrap">
                    <div className='ThemeThreeRecentImageWrap'>
                        {imageIdObj && imageIdObj.thumbsize && (
                            <picture>
                                <source media="(min-width: 768px)" srcSet={getImageUrl(imageIdObj.msid, imageIdObj.thumbsize, 760, 428, "", false, props.CONSTANTS, imagecroptype)} />
                                <source media="(max-width: 767px)" srcSet={getImageUrl(imageIdObj.msid, imageIdObj.thumbsize, 328, 185, "", false, props.CONSTANTS, imagecroptype)} />
                                <img alt={liveblogData.title} title={liveblogData.title} />
                            </picture>
                        )}
                    </div>
                    {/* {liveblogData && liveblogData?.synopsis && (
                        <div className="liveBlogSynopsisWrap">
                            <p><strong>Synopsis:</strong> {liveblogData?.mobilesynopsis}</p>
                        </div>
                    )} */}

                    {liveblogData?.mobilesynopsis && (
                        <div className="liveBlogSynopsisWrap">
                            <p>
                                <strong>Synopsis:</strong>{" "}
                                <span
                                    dangerouslySetInnerHTML={{ __html: liveblogData.mobilesynopsis }}
                                />
                            </p>
                        </div>
                    )}
                </div>
                <div className='liveBlogMainContentWrap'>
                    <div className='liveBlogSocialIcons blogShowDesktopSocial'>
                        <SocialShareTwo
                            shareUrl={URL_PROTOCAL + REQUEST_HOSTNAME + REQUEST_URL}
                            showFacebook={true}
                            showTwitter={true}
                            showLinkedin={true}
                            showWhatsapp={true}
                            showPintrest={true} />
                    </div>
                    <div className='liveBlogSummeryWrap'>
                        {liveblogData && liveblogData?.synopsis && (
                            <div className='liveBlogBigSynopsis'>{parse(liveblogData?.synopsis)}</div>
                        )}
                        <div className='liveBlogWrapper'>
                            {newUpdatesCount > 0 && (
                                <Button className='notificationButton'
                                    onClick={handleNotificationClick}
                                    disabled={loadingUpdates}
                                    endIcon={!loadingUpdates && <RefreshIcon />}
                                >
                                    {loadingUpdates
                                        ? 'Loading updates...'
                                        : `${String(newUpdatesCount).padStart(2, '0')} new update${newUpdatesCount > 1 ? 's' : ''}`}
                                </Button>
                            )}
                            {loading && <div className='loader'>Loading liveblog data...</div>}
                            {error && <div className='error'>{error}</div>}
                            {!loading && !error && renderLiveblogItems()}
                        </div>
                    </div>
                </div>


            </div>
        </CacheProvider>
    )
}

